import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MessageSquare, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { usePortal } from "../PortalContext";
import { ChatWindow } from "../components/ChatWindow";
import type { Conversation } from "../components/ChatWindow";

function formatDate(iso: string | null | undefined): string {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export default function ApplicationMessagesTab() {
    const navigate = useNavigate();
    const { applicationNumber } = useParams<{ applicationNumber: string }>();
    const { applications, conversations, conversationsLoading, refreshConversations } = usePortal();

    useEffect(() => {
        refreshConversations();
    }, [refreshConversations]);

    const application = applications.find(
        (a) => a.Application_Number === applicationNumber || a.Application_External_ID === applicationNumber
    );

    // Match conversation by number or external ID — both directions
    const portalConv = applicationNumber
        ? conversations.find((c) => {
              if (c.application_number && c.application_number === applicationNumber) return true;
              if (c.application_external_id && c.application_external_id === applicationNumber) return true;
              if (application) {
                  if (c.application_number && c.application_number === application.Application_Number) return true;
                  if (c.application_external_id && c.application_external_id === application.Application_External_ID) return true;
              }
              return false;
          })
        : null;

    const appExternalId =
        portalConv?.application_external_id ||
        application?.Application_External_ID ||
        "";

    const conversation: Conversation = portalConv
        ? {
              application_external_id: portalConv.application_external_id,
              application_name: portalConv.application_name,
              application_number: portalConv.application_number,
              messages: portalConv.messages.map((m) => ({
                  id: m.id,
                  external_id: m.external_id,
                  subject: m.subject,
                  message: m.message,
                  from_field: m.from_field,
                  to_field: null,
                  status: m.status,
                  read: m.read,
                  created_at: m.created_at,
              })),
          }
        : {
              application_external_id: appExternalId,
              application_name: application?.Applied_Program_Name || null,
              application_number: application?.Application_Number || null,
              messages: [],
          };

    const convStatus = portalConv?.is_closed ? "Closed" : "Active";
    const statusClass = portalConv?.is_closed
        ? "border-slate-200 text-slate-400 bg-slate-50"
        : "border-emerald-100 text-emerald-600 bg-emerald-50/50";

    function onBack() {
        if (applicationNumber) navigate(`/portal/applications/${applicationNumber}`);
        else navigate("/portal/applications");
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4 px-2">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onBack}
                    className="flex items-center gap-2 text-slate-500 hover:text-primary font-bold px-2 -ml-2"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {applicationNumber ? "Back to Application" : "Back to Applications"}
                </Button>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-2">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                        <MessageSquare className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="min-w-0">
                        <h3 className="text-xl font-serif font-black text-slate-900 tracking-tight">
                            {application?.Applied_Program_Name || "Messages"}
                        </h3>
                        <p className="text-xs text-slate-400 font-medium">{applicationNumber}</p>
                    </div>
                </div>
                <Badge
                    variant="outline"
                    className={`text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-widest border whitespace-nowrap ${statusClass}`}
                >
                    {convStatus}
                </Badge>
            </div>

            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-5">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Task Details
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {[
                        { label: "Subject",            value: portalConv?.subject || application?.Applied_Program_Name || "—" },
                        { label: "Status",             value: convStatus },
                        { label: "Type",               value: "Message" },
                        { label: "Updated",            value: formatDate(portalConv?.updated_at) },
                        { label: "Application",        value: application?.Applied_Program_Name },
                        { label: "Application Number", value: applicationNumber },
                    ].map((item) => (
                        <div key={item.label} className="space-y-1">
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{item.label}</p>
                            <p className="text-sm font-bold text-slate-800">{item.value || "—"}</p>
                        </div>
                    ))}
                </div>
            </div>

            {application && (
                <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-5">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                        Application
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {[
                            { label: "Full Name",       value: application.Applicant_Name },
                            { label: "Applied Term",    value: application.Applied_Term },
                            { label: "Applied Program", value: application.Applied_Program_Name },
                            { label: "Degree",          value: application.summary?.degree },
                            { label: "Stage",           value: application.Stage },
                            { label: "Application No",  value: application.Application_Number },
                        ].map((field) => (
                            <div key={field.label} className="space-y-1">
                                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{field.label}</p>
                                <p className="text-sm font-bold text-slate-800">{field.value || "—"}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 space-y-4">
                <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-900 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    Messages
                </h4>
                {conversationsLoading ? (
                    <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <p className="text-xs font-bold">Loading messages…</p>
                    </div>
                ) : !appExternalId ? (
                    <p className="text-sm text-slate-400 text-center py-8">No application found.</p>
                ) : (
                    <div style={{ height: "50vh", minHeight: 320 }} className="flex flex-col">
                        <ChatWindow conversation={conversation} onMessageSent={refreshConversations} />
                    </div>
                )}
            </div>
        </div>
    );
}
